/**
 * Responsible for showing a product notification.
 * @module modules/productNotificationShow
 */
import $ from 'jquery';
import { PRODUCT_BLOCK, DELIVERY_DATE_BLOCK, STOCK_DATE_BLOCK, ADD_TO_LIST_BLOCK } from '../lib/constants/pdp';
import getTemplateHTMLById from '../lib/get-template-html-by-id';

/**
 * Element class name
 * @type {string}
 */
export const EL_CLASS = 'notification';

/**
 * Prefix used on the template's id
 * @type {string}
 */
export const TEMPLATE_PREFIX = 'product-notifications';

/**
 * Choose the CTA based on attributes.
 * @private
 * @param {object} data - Product data object
 * @param {object} data.chosen - Selected product size, including relevant data
 * @return {string} Selected Id
 */
function getId({ chosen }) {
  if (chosen['defect-disclaimer'] && !chosen['final-sale']) {
    return 'defect-not-final-sale';
  } else if (chosen['defect-disclaimer']) {
    return 'defect';
  } else if (chosen['final-sale']) {
    return 'final-sale';
  } else if (chosen.preorder) {
    return 'preorder';
  } else if (chosen['one-left']) {
    return 'one-left';
  } else if (chosen['sample-defect']) {
    return 'sample-defect';
  } else if (chosen['removed-item']) {
    return 'removed-item';
  }
  return '';
}

/**
 * Toggles Show/Hide for Delivery date
 * @private
 * @param {string} notification - selected notification
 */
function toggleDelivery(notification) {
  const $deliveryEl = $(`.${PRODUCT_BLOCK}__${DELIVERY_DATE_BLOCK}`);
  const $stockEl = $(`.${PRODUCT_BLOCK}__${STOCK_DATE_BLOCK}`);

  if (notification === 'preorder') {
    $deliveryEl.hide();
    $stockEl.show();
  } else {
    $deliveryEl.show();
    $stockEl.hide();
  }
}

/**
 * AB Test 1490
 * Toggles Show/Hide for List Link
 * @private
 * @param {string} notification - selected notification
 */
function toggleAddToList(notification) {
  const $myListLink = $(`.${PRODUCT_BLOCK}__${ADD_TO_LIST_BLOCK}`);
  const hasAddMyListLink = $myListLink.length;

  if (hasAddMyListLink) {
    if (notification === 'preorder') {
      $myListLink.hide();
    } else {
      $myListLink.show();
    }
  }
}

/**
 * Default options for updateCTA
 * @type {object}
 * @prop {Function} getId - function to return the CTA id
 * @prop {Function} getTemplate - function to return the template
 */
const DEFAULT_OPTIONS = {
  getId,
  getTemplate: getTemplateHTMLById,
};

/**
 * Show notification based on chosen option.
 * @param {object} data - Product data object
 * @param {HTMLElement} data.wrap - The .product wrap
 * @param {string} data.wrapBlockClass - Class of wrap HTML Element
 * @param {boolean} data.allOnSale - Whether all sizes are on sale
 * @see DEFAULT_OPTIONS
 * @return {jQuery} Updated element
 */
export default function showNotification(data, options = {}) {
  const { wrap, wrapBlockClass } = data;
  const {
    getId: getIdFunc,
    getTemplate: getTemplateFunc,
  } = $.extend({}, DEFAULT_OPTIONS, options);

  // find the element
  const $el = $(wrap).find(`.${wrapBlockClass}__${EL_CLASS}`);
  if (!$el.length) {
    throw new Error(`Element not found: .${wrapBlockClass}__${EL_CLASS}`);
  }

  // get the proper html
  const id = getIdFunc(data);
  const html = id ? getTemplateFunc(`${TEMPLATE_PREFIX}.${id}`) : '';

  if (data.chosen['inventory-disclaimer'] && html) {
    const disclaimerEl = html.querySelector('.inventory-disclaimer');
    if (disclaimerEl) {
      disclaimerEl.innerHTML = data.chosen['inventory-disclaimer'];
    }
  }
  // update the element
  $el.html(html);
  if (id !== 'one-left' && id !== 'preorder' && data.chosen['one-left']) {
    const oneLeftId = 'one-left';
    const oneLeftHtml = getTemplateFunc(`${TEMPLATE_PREFIX}.${oneLeftId}`);
    $el.append(oneLeftHtml);
  }

  if (id !== 'removed-item' && data.chosen['removed-item']) {
    const removedItemId = 'removed-item';
    const removedItemHtml = getTemplateFunc(`${TEMPLATE_PREFIX}.${removedItemId}`);
    $el.append(removedItemHtml);
  }

  if (id === 'final-sale' && data.chosen.preorder) {
    const preorderId = 'preorder';
    const preorderHtml = getTemplateFunc(`${TEMPLATE_PREFIX}.${preorderId}`);
    $el.append(preorderHtml);
    toggleDelivery(preorderId);
    toggleAddToList(preorderId);
  } else {
    toggleDelivery(id);
    toggleAddToList(id);
  }

  return $el;
}



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/product-notification-show.js